import * as constants from '../constants';

const nms = [ constants.NAMESPACE, 'store' ].join('/');
const log = require('debug')('@mycure/vue-plugins/' + nms);

/**
 * @callback ProceedFn
 * @param {string} [path]
 * @returns {void}
 */

/**
 * @typedef {Object} GuardOpts
 * @property {string} namespace
 * @property {import('vue-router/types').default} router
 * @property {import('vuex').Store} store
 * @property {string} ssoURL
 * @property {(url: string) => string} ssoURLFn
 * @property {string} ssoTargetPath target path for sso signin
 * @property {(route: import('vue-router').Route, proceed: ProceedFn, opts: GuardOpts) => void} signinHandler
 * @property {(route: import('vue-router').Route, opts: GuardOpts) => void} signoutHandler
 * @property {(error: Error, proceed: ProceedFn, opts: GuardOpts) => void} errorHandler
 */

/**
 * @param {import('vue-router').Route} to
 * @param {GuardOpts} opts
 */
function ssoSignin (to, opts) {
  /** @type {string} */
  let baseURL = opts.ssoURL || '';
  if (!baseURL.endsWith('/')) baseURL += '/';
  let targetPath = opts.ssoTargetPath || to.path;
  if (targetPath.startsWith('/')) targetPath = targetPath.substring(1);
  const currentLocation = [ window.location.origin, targetPath ].join('/');

  log('sso login @', baseURL, 'back to', currentLocation);

  const signinURL = baseURL + '?target=' + currentLocation;
  window.location.href = typeof opts.ssoURLFn === 'function' ? opts.ssoURLFn(signinURL) : signinURL;
}

/**
 * @param {import('vue').Component} Vue
 * @param {GuardOpts} opts
 */
function install (Vue, opts) {
  opts = Object.assign({ namespace: constants.NAMESPACE }, opts);
  const namespace = opts.namespace;
  const router = opts.router;
  const store = opts.store;
  if (!router) throw new Error(`${nms}: Vue router is required`);
  if (!store) throw new Error(`${nms}: Vuex store is required`);
  if (!store.state[namespace]) throw new Error(`${nms}: Auth store plugin must be installed`);

  let currentUserSub = null;
  router.beforeEach(async (to, from, next) => {
    const match = to.matched.some(r => r.meta.requiresAuth);
    if (!match) return next();
    try {
      log('guarding path', to.fullPath);

      const currentUser = await store.dispatch(namespace + '/loadCurrentUser');

      log('currentUser', currentUser);

      const proceed = (path = { path: to.path, replace: true }) => {
        // handle forced logout
        if (currentUserSub) currentUserSub();
        currentUserSub = store.watch(s => s[namespace].currentUser, (val, old) => {
          if (!old || val) return;
          currentUserSub();
          log('currentUser was unset @ path', to.fullPath);
          if (typeof opts.signoutHandler === 'function') return opts.signoutHandler(to, opts);
          router.replace('/');
        });
        next(path);
      };

      // already logged in
      if (currentUser) return proceed(true);

      // custom handling
      if (typeof opts.signinHandler === 'function') return opts.signinHandler(to, proceed, opts);

      // handle sso
      if (opts.ssoURL) {
        if (!to.query.token) return ssoSignin(to, opts);

        log('sso token signin', to.query.token);

        await store.dispatch(namespace + '/signin', { strategy: 'jwt', accessToken: to.query.token });
        return proceed();
      }

      // no handling
      throw new Error(`Authentication required in route ${to.path}`);
    } catch (error) {
      log('signin error', error);

      const proceed = (path = error) => {
        // handle sso
        if (opts.ssoURL) return ssoSignin(to, opts);

        // no handling
        next(path);
      };

      // custom handling
      if (typeof opts.errorHandler === 'function') return opts.errorHandler(error, proceed, opts);

      proceed();
    }
  });
}

export default { install };
